import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TrialOutreachPill from './TrialOutreachPill';
import { outcomeOption, describeOutcome } from '../trialOutreachOutcomes';

const STAGE_LABELS = { active: 'Teste em andamento', expired: 'Teste vencido' };
const VARIANT_LABELS = { A: 'A · sem uso', B: 'B · com uso' };
const CHANNEL_LABELS = { whatsapp: 'WhatsApp', email: 'E-mail' };

/**
 * 'Y-m-d H:i:s' (fuso do servidor) -> "16/10/2026 10:00".
 * @param {string|null} value
 * @returns {string}
 */
function fmtDateTime(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * @param {Object} item
 * @returns {React.ReactNode}
 */
function ResultPill({ item }) {
  if (item.status === 'queued') return <TrialOutreachPill tone="green" label="WhatsApp na fila" />;
  if (item.status === 'sent') {
    if (item.fallback_reason) {
      return (
        <Tooltip title={item.fallback_reason}>
          <Box component="span"><TrialOutreachPill tone="orange" label="Foi por e-mail no lugar do WhatsApp" /></Box>
        </Tooltip>
      );
    }
    return <TrialOutreachPill tone="green" label="Enviado" />;
  }
  return <TrialOutreachPill tone="red" label={`Falhou: ${item.error || 'erro ao enviar'}`} />;
}

/**
 * TrialOutreachHistoryRow - Uma mensagem do contato com testes, com o texto enviado
 * @param {Object} props
 * @param {Object} props.item linha de history.php
 * @param {number} props.colSpan
 * @param {Function} props.onMarkOutcome recebe o item para marcar o resultado da empresa
 */
function TrialOutreachHistoryRow({ item, colSpan, onMarkOutcome }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2">{fmtDateTime(item.created_at)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {item.by_routine ? 'Rotina' : item.single ? 'Enviar mensagem' : 'Envio manual'}
          </Typography>
        </TableCell>

        <TableCell>
          {item.company_id !== null ? (
            <Link component={RouterLink} to={`/admin/companies/${item.company_id}`} underline="hover" sx={{ fontWeight: 600 }}>
              {`#${item.company_id} ${item.company_name || ''}`}
            </Link>
          ) : (
            item.company_name || '—'
          )}
          {item.company_id !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {outcomeOption(item.outcome?.outcome) && (
                <Tooltip title={item.outcome.note || ''}>
                  <Box component="span">
                    <TrialOutreachPill tone={outcomeOption(item.outcome.outcome).tone} label={describeOutcome(item.outcome)} />
                  </Box>
                </Tooltip>
              )}
              <Button
                size="small"
                onClick={() => onMarkOutcome(item)}
                sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0, fontSize: '0.75rem' }}
              >
                {item.outcome ? 'Alterar resultado' : 'Marcar resultado'}
              </Button>
            </Box>
          )}
        </TableCell>

        <TableCell>
          {item.stage ? (
            <TrialOutreachPill tone={item.stage === 'expired' ? 'orange' : 'blue'} label={STAGE_LABELS[item.stage] || item.stage} />
          ) : (
            <Typography variant="body2" color="text.secondary">Mensagem avulsa</Typography>
          )}
          {item.variant && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {VARIANT_LABELS[item.variant] || item.variant}
            </Typography>
          )}
        </TableCell>

        <TableCell>
          <Typography variant="body2">{CHANNEL_LABELS[item.channel] || '—'}</Typography>
          {item.to && (
            <Typography variant="caption" color="text.secondary">{item.to}</Typography>
          )}
        </TableCell>

        <TableCell>
          <ResultPill item={item} />
        </TableCell>

        <TableCell align="right">
          <Tooltip title={open ? 'Esconder mensagem' : 'Ver a mensagem'}>
            <IconButton size="small" onClick={() => setOpen((prev) => !prev)} aria-label="Ver a mensagem">
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={colSpan} sx={{ py: 0, ...(open ? {} : { borderBottom: 'none' }) }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, p: 2, borderRadius: '8px', bgcolor: '#F8F9FC', border: '1px solid #E8ECF0' }}>
              {item.subject && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Assunto:</strong> {item.subject}
                </Typography>
              )}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.message || '—'}</Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default TrialOutreachHistoryRow;
